import { useState, useCallback, useMemo } from 'react';
import {
  ExportAction,
  ExportOptions,
  ExportModalProps,
  ExportModalState,
  SalesExportParams,
  PurchaseExportParams,
  POSExportParams,
  LedgerExportParams,
  TDSExportParams,
  TCSExportParams,
  CrDrExportParams,
  PartyExportOptions,
  PaymentReceiptExportParams,
} from '../types';
import { useNavigate } from 'react-router-dom';

const initialState: ExportModalState = {
  open: false,
  action: null,
  exportOptions: {},
};

export function useExportModal() {
  const [state, setState] = useState<ExportModalState>(initialState);
  const navigate = useNavigate();

  const openExport = useCallback(
    (
      actionType: ExportAction,
      options?: ExportOptions & { startDate?: number | null; endDate?: number | null; accounttypeflag?: number | null }
    ) => {
      setState({
        open: true,
        action: actionType,
        exportOptions: options || {},
      });
    },
    []
  );

  /**
   * Opens the Sales Invoice Export modal with the specified parameters.
   *
   * @param params - Sales export parameters
   * @param params.selectedArray - Array of selected invoice IDs to export
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   * @param params.filters - Optional filters to apply to the export
   *
   * @example
   * ```tsx
   * const { ExportSales } = useExportModal();
   *
   * // Export selected invoices
   * ExportSales({
   *   selectedArray: [1, 2, 3],
   *   startDate: 1609459200, // Unix timestamp
   *   endDate: 1640995200,
   *   filters: { status: 'active' }
   * });
   * ```
   */
  const ExportSales = useCallback(
    (params: SalesExportParams) => {
      openExport('SALESEXPORT', {
        selectedArray: params.selectedArray,
        startDate: params.startDate,
        endDate: params.endDate,
        filters: params.filters,
      });
    },
    [openExport]
  );

  /**
   * Opens the Purchase Invoice Export modal with the specified parameters.
   *
   * @param params - Purchase export parameters
   * @param params.selectedArray - Array of selected invoice IDs to export
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   * @param params.filters - Optional filters to apply to the export
   * @param params.type - Optional type to apply to the export
   *
   * @example
   * ```tsx
   * const { ExportPurchase } = useExportModal();
   *
   * // Export selected purchase invoices
   * ExportPurchase({
   *   selectedArray: [10, 11, 12],
   *   startDate: 1609459200,
   *   endDate: 1640995200,
   *   filters: { vendor: 'Supplier A' }
   * });
   * ```
   */
  const ExportPurchase = useCallback(
    (params: PurchaseExportParams) => {
      openExport('PURCHASEEXPORT', {
        selectedArray: params.selectedArray,
        startDate: params.startDate,
        endDate: params.endDate,
        filters: params.filters,
      });
    },
    [openExport]
  );

  const ExportItem = useCallback(
    (params: PurchaseExportParams) => {
      openExport('ITEMSEXPORT', {
        selectedArray: params.selectedArray,
        startDate: params.startDate,
        endDate: params.endDate,
        filters: params.filters,
      });
    },
    [openExport]
  );

  const ExportPaymentReceipt = useCallback(
    (params: PaymentReceiptExportParams) => {
      openExport('PAYMENTRECEIPTEXPORT', {
        transactionOption: params.transactionOption,
        selectedArray: params.selectedArray,
        startDate: params.startDate,
        endDate: params.endDate,
        filters: params.filters,
      });
    },
    [openExport]
  );
  /**
   * Opens the POS Invoice Export modal with the specified parameters.
   *
   * @param params - POS export parameters
   * @param params.selectedArray - Array of selected POS invoice IDs to export
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   * @param params.filters - Optional filters to apply to the export
   *
   * @example
   * ```tsx
   * const { ExportPOS } = useExportModal();
   *
   * // Export selected POS invoices
   * ExportPOS({
   *   selectedArray: [20, 21, 22],
   *   startDate: 1609459200,
   *   endDate: 1640995200,
   *   filters: { location: 'Store 1' }
   * });
   * ```
   */
  const ExportPOS = useCallback(
    (params: POSExportParams) => {
      openExport('POSEXPORT', {
        selectedArray: params.selectedArray,
        startDate: params.startDate,
        endDate: params.endDate,
        filters: params.filters,
      });
    },
    [openExport]
  );

  /**
   * Opens the Ledgers Export modal with the specified parameters.
   *
   * @param params - Ledger export parameters
   * @param params.ledgerOptions - Ledger configuration options
   * @param params.ledgerOptions.openingBalance - Whether to include opening balance (0 or 1)
   * @param params.ledgerOptions.AccountDetails - Optional array of account details with worksheet names
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   *
   * @example
   * ```tsx
   * const { ExportLedgers } = useExportModal();
   *
   * // Export ledgers with opening balance
   * ExportLedgers({
   *   ledgerOptions: {
   *     openingBalance: 1,
   *     AccountDetails: [
   *       { accountId: 1, worksheetName: 'Account 1' },
   *       { accountId: 2, worksheetName: 'Account 2' }
   *     ]
   *   },
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   * ```
   */
  const ExportLedgers = useCallback(
    (params: LedgerExportParams) => {
      console.log(params, 'pararam');

      openExport('LEDGERSEXPORT', {
        ledgerOptions: params.ledgerOptions,
        startDate: params.startDate,
        endDate: params.endDate,
        accounttypeflag: params.accounttypeflag,
      });
    },
    [openExport]
  );

  /**
   * Opens the TDS (Tax Deducted at Source) Export modal with the specified parameters.
   *
   * @param params - TDS export parameters
   * @param params.tdstcsoption - TDS option type: 'TDSPAY' (payable) or 'TDSREC' (receivable)
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   *
   * @example
   * ```tsx
   * const { ExportTDS } = useExportModal();
   *
   * // Export TDS payable
   * ExportTDS({
   *   tdstcsoption: 'TDSPAY',
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   *
   * // Export TDS receivable
   * ExportTDS({
   *   tdstcsoption: 'TDSREC',
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   * ```
   */
  const ExportTDS = useCallback(
    (params: TDSExportParams) => {
      openExport('TDSEXPORT', {
        tdstcsoption: params.tdstcsoption,
        startDate: params.startDate,
        endDate: params.endDate,
      });
    },
    [openExport]
  );

  /**
   * Opens the TCS (Tax Collected at Source) Export modal with the specified parameters.
   *
   * @param params - TCS export parameters
   * @param params.tdstcsoption - TCS option type: 'TCSPAY' (payable) or 'TCSREC' (receivable)
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   *
   * @example
   * ```tsx
   * const { ExportTCS } = useExportModal();
   *
   * // Export TCS payable
   * ExportTCS({
   *   tdstcsoption: 'TCSPAY',
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   *
   * // Export TCS receivable
   * ExportTCS({
   *   tdstcsoption: 'TCSREC',
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   * ```
   */
  const ExportTCS = useCallback(
    (params: TCSExportParams) => {
      openExport('TCSEXPORT', {
        tdstcsoption: params.tdstcsoption,
        startDate: params.startDate,
        endDate: params.endDate,
      });
    },
    [openExport]
  );

  /**
   * Opens the Credit/Debit Note Export modal with the specified parameters.
   *
   * @param params - Credit/Debit export parameters
   * @param params.crDrOption - Credit/Debit note configuration
   * @param params.crDrOption.noteType - Type of note: 'CreditNote' or 'DebitNote'
   * @param params.startDate - Optional start date for the export (Unix timestamp)
   * @param params.endDate - Optional end date for the export (Unix timestamp)
   *
   * @example
   * ```tsx
   * const { ExportCrDr } = useExportModal();
   *
   * // Export Credit Notes
   * ExportCrDr({
   *   crDrOption: { noteType: 'CreditNote' },
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   *
   * // Export Debit Notes
   * ExportCrDr({
   *   crDrOption: { noteType: 'DebitNote' },
   *   startDate: 1609459200,
   *   endDate: 1640995200
   * });
   * ```
   */
  const ExportCrDr = useCallback(
    (params: CrDrExportParams) => {
      openExport('CRDREXPORT', {
        crDrOption: params.crDrOption,
        startDate: params.startDate,
        endDate: params.endDate,
        selectedArray: params.selectedArray,
      });
    },
    [openExport]
  );

  const ExportCustomerSupplier = useCallback(
    (params: PartyExportOptions) => {
      console.log(params, 'params');

      openExport('PARTYEXPORT', {
        selectedArray: params.selectedArray,
        partyOption: params.partyOption,
        startDate: params.startDate,
        endDate: params.endDate,
      });
    },
    [openExport]
  );

  const handleClose = useCallback(() => {
    setState(initialState);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.delete('startDate');
    currentParams.delete('endDate');

    const newUrl = currentParams.toString() ? `${window.location.pathname}?${currentParams.toString()}` : window.location.pathname;

    // Update URL instantly
    navigate(newUrl, { replace: true });

    // Clear internal state
    // setStartDateUrl(null);
    // setEndDateUrl(null);/
  }, []);

  // Return modal props that can be spread directly into ExportModal component
  const exportModalProps: ExportModalProps = useMemo(
    () => ({
      open: state.open,
      onClose: handleClose,
      action: state.action,
      exportOptions: state.exportOptions,
    }),
    [state, handleClose]
  );

  return {
    // Spreadable props for ExportModal component
    exportModalProps,
    // Individual export functions
    ExportSales,
    ExportLedgers,
    ExportPurchase,
    ExportPOS,
    ExportTDS,
    ExportTCS,
    ExportCrDr,
    ExportItem,
    ExportPaymentReceipt,
    ExportCustomerSupplier,
    // Individual state values (for backward compatibility or direct access)
    open: state.open,
    action: state.action,
    exportOptions: state.exportOptions,

    handleClose,
  };
}
